import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useServices } from '../hooks/useAirtable';
import nocobaseService from '../services/nocobaseService';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TestServicesPage = () => {
  const { currentLanguage, isRTL } = useLanguage();
  const activeLanguage = currentLanguage;
  const activeIsRTL = isRTL;
  
  const { services, loading: servicesLoading } = useServices(true, activeLanguage);
  const [subservicesMap, setSubservicesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedServices, setExpandedServices] = useState({});

  // Fetch all subservices for all services
  useEffect(() => {
    const fetchAllSubservices = async () => {
      if (!services || services.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const map = {};

      // Fetch subservices for each service
      for (const service of services) {
        try {
          console.log(`\n🔍 Fetching subservices for service: ${service.title} (ID: ${service.id})`);
          const subservices = await nocobaseService.getSubservices(service.id, false, activeLanguage);
          map[service.id] = subservices || [];
          console.log(`✅ Found ${subservices?.length || 0} subservice(s) for service ${service.id}`);
        } catch (error) {
          console.error(`❌ Failed to fetch subservices for service ${service.id}:`, error);
          map[service.id] = [];
        }
      }

      setSubservicesMap(map);
      setLoading(false);
    };

    fetchAllSubservices();
  }, [services, activeLanguage]);

  const toggleService = (serviceId) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  if (servicesLoading || loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }} dir={activeIsRTL ? 'rtl' : 'ltr'}>
        <Header />
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '40px 20px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '16px' }}>
              {activeLanguage === 'ar' ? 'جاري التحميل...' : 'Loading...'}
            </div>
            <div style={{ fontSize: '16px', color: '#64748b' }}>
              {activeLanguage === 'ar' ? 'جاري جلب الخدمات والخدمات الفرعية...' : 'Fetching services and subservices...'}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }} dir={activeIsRTL ? 'rtl' : 'ltr'}>
      <Header />
      
      <main style={{ flex: 1, padding: '40px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Page Header */}
          <div style={{ 
            marginBottom: '40px',
            textAlign: 'center',
            paddingBottom: '30px',
            borderBottom: '3px solid #1a1a2e'
          }}>
            <h1 style={{
              fontSize: '42px',
              fontWeight: '700',
              color: '#1a1a2e',
              marginBottom: '16px'
            }}>
              {activeLanguage === 'ar' ? 'صفحة تجربة الخدمات' : 'Services Test Page'}
            </h1>
            <p style={{
              fontSize: '18px',
              color: '#64748b',
              margin: 0
            }}>
              {activeLanguage === 'ar' 
                ? 'عرض جميع الخدمات الرئيسية والخدمات الفرعية المترابطة' 
                : 'Display all main services and their related subservices'}
            </p>
          </div>

          {/* Services List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {services && services.length > 0 ? (
              services.map((service, index) => {
                const subservices = subservicesMap[service.id] || [];
                const isExpanded = expandedServices[service.id] || false;

                return (
                  <div
                    key={service.id}
                    style={{
                      background: 'white',
                      borderRadius: '16px',
                      border: '2px solid #e2e8f0',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {/* Service Header */}
                    <button
                      onClick={() => toggleService(service.id)}
                      style={{
                        width: '100%',
                        padding: '28px',
                        background: isExpanded ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' : 'white',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        textAlign: activeIsRTL ? 'right' : 'left',
                        gap: '20px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!isExpanded) {
                          e.currentTarget.style.background = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isExpanded) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: 1 }}>
                        {/* Service Number */}
                        <div style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          background: isExpanded 
                            ? 'rgba(255, 255, 255, 0.2)' 
                            : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                          color: isExpanded ? 'white' : 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          fontWeight: '700',
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </div>

                        {/* Service Info */}
                        <div style={{ flex: 1, textAlign: activeIsRTL ? 'right' : 'left' }}>
                          <h2 style={{
                            fontSize: '28px',
                            fontWeight: '700',
                            color: isExpanded ? 'white' : '#1a1a2e',
                            margin: '0 0 8px 0',
                            lineHeight: '1.3'
                          }}>
                            {service.title}
                          </h2>
                          <div style={{
                            fontSize: '16px',
                            color: isExpanded ? 'rgba(255, 255, 255, 0.9)' : '#64748b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            flexWrap: 'wrap'
                          }}>
                            <span>
                              {subservices.length} {activeLanguage === 'ar' ? 'خدمة فرعية' : 'subservice(s)'}
                            </span>
                            {service.id && (
                              <span style={{
                                padding: '4px 12px',
                                background: isExpanded ? 'rgba(255, 255, 255, 0.2)' : '#f1f5f9',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontFamily: 'monospace'
                              }}>
                                ID: {service.id}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Toggle Icon */}
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={isExpanded ? 'white' : '#1a1a2e'}
                        strokeWidth="2.5"
                        style={{
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                          flexShrink: 0
                        }}
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>

                    {/* Subservices List */}
                    {isExpanded && (
                      <div style={{
                        padding: '0 28px 28px 28px',
                        background: 'white',
                        borderTop: '1px solid #e2e8f0'
                      }}>
                        {subservices.length > 0 ? (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '16px',
                            marginTop: '24px'
                          }}>
                            {subservices.map((subservice, subIndex) => (
                              <div
                                key={subservice.id}
                                style={{
                                  padding: '20px',
                                  background: '#f8fafc',
                                  borderRadius: '12px',
                                  border: '1px solid #e2e8f0',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#f1f5f9';
                                  e.currentTarget.style.borderColor = '#1a1a2e';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#f8fafc';
                                  e.currentTarget.style.borderColor = '#e2e8f0';
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                                  {/* Subservice Number */}
                                  <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '16px',
                                    fontWeight: '700',
                                    flexShrink: 0
                                  }}>
                                    {subIndex + 1}
                                  </div>

                                  {/* Subservice Info */}
                                  <div style={{ flex: 1 }}>
                                    <h3 style={{
                                      fontSize: '20px',
                                      fontWeight: '600',
                                      color: '#1a1a2e',
                                      margin: '0 0 8px 0',
                                      textAlign: activeIsRTL ? 'right' : 'left'
                                    }}>
                                      {subservice.title}
                                    </h3>
                                    
                                    {subservice.description && (
                                      <p style={{
                                        fontSize: '15px',
                                        color: '#64748b',
                                        margin: '0 0 12px 0',
                                        lineHeight: '1.6',
                                        textAlign: activeIsRTL ? 'right' : 'left'
                                      }}>
                                        {subservice.description.substring(0, 150)}
                                        {subservice.description.length > 150 ? '...' : ''}
                                      </p>
                                    )}

                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '12px',
                                      flexWrap: 'wrap',
                                      fontSize: '13px',
                                      color: '#94a3b8'
                                    }}>
                                      {subservice.id && (
                                        <span style={{
                                          padding: '4px 10px',
                                          background: '#e2e8f0',
                                          borderRadius: '6px',
                                          fontFamily: 'monospace'
                                        }}>
                                          ID: {subservice.id}
                                        </span>
                                      )}
                                      {subservice.Subservices !== undefined && subservice.Subservices !== null && (
                                        <span style={{
                                          padding: '4px 10px',
                                          background: '#dbeafe',
                                          borderRadius: '6px',
                                          fontFamily: 'monospace'
                                        }}>
                                          Service ID: {subservice.Subservices}
                                        </span>
                                      )}
                                      {subservice.status && (
                                        <span style={{
                                          padding: '4px 10px',
                                          background: subservice.status.toLowerCase() === 'live' ? '#dcfce7' : '#fee2e2',
                                          borderRadius: '6px',
                                          color: subservice.status.toLowerCase() === 'live' ? '#166534' : '#991b1b'
                                        }}>
                                          {subservice.status}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{
                            padding: '40px',
                            textAlign: 'center',
                            color: '#94a3b8',
                            fontSize: '16px'
                          }}>
                            {activeLanguage === 'ar' 
                              ? 'لا توجد خدمات فرعية مرتبطة بهذه الخدمة' 
                              : 'No subservices linked to this service'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div style={{
                padding: '60px',
                textAlign: 'center',
                background: 'white',
                borderRadius: '16px',
                border: '2px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '24px', color: '#64748b', marginBottom: '12px' }}>
                  {activeLanguage === 'ar' ? 'لا توجد خدمات' : 'No services found'}
                </div>
                <div style={{ fontSize: '16px', color: '#94a3b8' }}>
                  {activeLanguage === 'ar' 
                    ? 'لم يتم العثور على أي خدمات في قاعدة البيانات' 
                    : 'No services were found in the database'}
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          <div style={{
            marginTop: '40px',
            padding: '24px',
            background: 'white',
            borderRadius: '16px',
            border: '2px solid #e2e8f0',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a2e', marginBottom: '12px' }}>
              {activeLanguage === 'ar' ? 'ملخص' : 'Summary'}
            </div>
            <div style={{ fontSize: '16px', color: '#64748b' }}>
              {activeLanguage === 'ar' 
                ? `إجمالي الخدمات الرئيسية: ${services?.length || 0} | إجمالي الخدمات الفرعية: ${Object.values(subservicesMap).reduce((sum, subs) => sum + (subs?.length || 0), 0)}`
                : `Total Main Services: ${services?.length || 0} | Total Subservices: ${Object.values(subservicesMap).reduce((sum, subs) => sum + (subs?.length || 0), 0)}`}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TestServicesPage;

